import { Lightbulb, Ruler } from 'lucide-react'
import { MATERIALS, calculateDormer, formatArea, formatMoney } from '@/lib/calculator'
import type { DormerInputs } from '@/lib/calculator'

interface Example {
  title: string
  scenario: string
  input: DormerInputs
}

const EXAMPLES: Example[] = [
  {
    title: 'Small gable-style window dormer',
    scenario: 'A bright reading nook in a 1930s semi-detached attic. Mostly about light and curb appeal.',
    input: { width: 6, depth: 5, height: 3.5, pitch: 8, materialId: 'vinyl' },
  },
  {
    title: 'Standard shed dormer bedroom',
    scenario: 'A rear shed dormer that turns a cramped attic into a proper bedroom with headroom.',
    input: { width: 12, depth: 8, height: 5, pitch: 4, materialId: 'fiber-cement' },
  },
  {
    title: 'Full-width master suite dormer',
    scenario: 'A wide premium shed dormer with metal roofing, adding a bedroom and ensuite bathroom.',
    input: { width: 20, depth: 10, height: 6, pitch: 3, materialId: 'metal' },
  },
]

const TIPS = [
  'Measure twice, from inside the attic. Rafter spacing, existing ceiling heights and chimney positions change what fits.',
  'Keep the dormer pitch at 3:12 or steeper. Shallower roofs need specialist membranes and cost more to maintain.',
  'Match the cladding to your house. Mismatched materials are a common reason planning officers refuse an application.',
  'Get three itemized quotes and compare them line by line against the cost breakdown here.',
  'Plan for a 10 to 15 percent contingency. Old roofs hide rot, wiring and structural surprises.',
  'Build the dormer on the rear or side slope when you can. It is cheaper to approve and keeps the front elevation clean.',
  'Combine the dormer with a re-roof if your roof is nearing the end of its life. You save scaffolding and labor.',
  'Ask for an energy performance plan. Dormers lose heat on three sides, so insulation quality matters.',
]

function CalculatorContent() {
  return (
    <div className="space-y-12">
      <section aria-labelledby="formula-heading" className="card p-6 sm:p-8">
        <div className="mb-4 flex items-center gap-3">
          <Ruler className="h-6 w-6 text-blueprint-600" aria-hidden="true" />
          <h2 id="formula-heading" className="text-2xl sm:text-3xl">
            How the dormer calculator works
          </h2>
        </div>
        <p className="mb-5 text-lg">
          The calculator converts your four measurements into surface areas, then multiplies those areas by material and labor rates. Here are the formulas it uses, so you can check any result by hand.
        </p>
        <ol className="space-y-3">
          {[
            ['Roof rise', 'rise = depth x pitch / 12'],
            ['Rafter (slope) length', 'slope = square root of (depth squared + rise squared)'],
            ['Roof area', 'roof = width x (slope + 0.5 ft overhang)'],
            ['Wall area', 'walls = width x height + 2 x depth x (height + rise / 2)'],
            ['Floor area', 'floor = width x depth'],
            ['Order quantities', 'quantity = area x 1.10 waste allowance'],
            ['Total cost', 'total = (labor + materials) x 1.10 contingency x 1.05 design and permits'],
          ].map(([label, formula], i) => (
            <li key={label} className="flex gap-3">
              <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-ink text-sm font-bold text-white">
                {i + 1}
              </span>
              <div>
                <p className="font-bold text-ink">{label}</p>
                <p className="mt-0.5 overflow-x-auto rounded-md bg-steel-100 px-3 py-1.5 font-mono text-sm text-blueprint-800">
                  {formula}
                </p>
              </div>
            </li>
          ))}
        </ol>
        <p className="mt-5 text-sm text-steel-500">
          Pitch is entered as rise per 12 inches of run, for example 4 means 4:12 (about 18.4 degrees). Lengths are converted to feet internally, so metric input gives the same answer.
        </p>
      </section>

      <section aria-labelledby="examples-heading">
        <h2 id="examples-heading" className="text-2xl sm:text-3xl">
          Three worked examples
        </h2>
        <p className="mt-2 max-w-2xl text-lg">
          Real-world sizes calculated with the same engine as the tool above. Use them as a reference for your own project.
        </p>
        <div className="mt-6 grid gap-5 md:grid-cols-3">
          {EXAMPLES.map((ex, i) => {
            const r = calculateDormer(ex.input)
            const mat = MATERIALS.find(m => m.id === ex.input.materialId)
            return (
              <article key={ex.title} className="card flex flex-col p-5">
                <p className="eyebrow">Example {i + 1}</p>
                <h3 className="mt-1 text-xl">{ex.title}</h3>
                <p className="mt-2 text-sm text-steel-600">{ex.scenario}</p>
                <dl className="mt-4 space-y-1.5 text-sm">
                  <Row k="Size" v={`${ex.input.width} x ${ex.input.depth} x ${ex.input.height} ft`} />
                  <Row k="Pitch" v={`${ex.input.pitch}:12`} />
                  <Row k="Material" v={mat?.label.split(' + ')[0] ?? ''} />
                  <Row k="Floor area" v={formatArea(r.floorArea, 'imperial')} />
                  <Row k="Roof area" v={formatArea(r.roofArea, 'imperial')} />
                  <Row k="Wall area" v={formatArea(r.wallArea, 'imperial')} />
                </dl>
                <div className="mt-auto rounded-xl bg-blueprint-50 p-4 pt-4">
                  <p className="text-xs font-bold uppercase tracking-wide text-blueprint-700">Estimated total</p>
                  <p className="text-2xl font-extrabold text-ink">{formatMoney(r.total)}</p>
                  <p className="text-xs text-steel-600">
                    Range {formatMoney(r.low)} to {formatMoney(r.high)}
                  </p>
                </div>
              </article>
            )
          })}
        </div>
      </section>

      <section aria-labelledby="tips-heading" className="rounded-2xl bg-signal-light p-6 sm:p-8">
        <div className="mb-4 flex items-center gap-3">
          <Lightbulb className="h-6 w-6 text-signal-dark" aria-hidden="true" />
          <h2 id="tips-heading" className="text-2xl sm:text-3xl">
            Pro tips for a better estimate
          </h2>
        </div>
        <ul className="grid gap-3 md:grid-cols-2">
          {TIPS.map(t => (
            <li key={t} className="flex gap-3 rounded-xl bg-white/70 p-4 text-steel-800">
              <span aria-hidden="true" className="mt-2 h-2 w-2 shrink-0 rounded-full bg-signal-dark" />
              <span>{t}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  )
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between gap-3 border-b border-steel-100 pb-1">
      <dt className="text-steel-500">{k}</dt>
      <dd className="text-right font-semibold text-ink">{v}</dd>
    </div>
  )
}

export default CalculatorContent
